/**
 * i18n — react-i18next Setup (DE + EN)
 *
 * - Default/Fallback: Deutsch (DE). DE-Übersetzungen = die ursprünglichen Strings,
 *   damit gerenderte Ausgabe unverändert bleibt (bestehende Tests bleiben grün).
 * - Namespaces werden per Vite-Glob automatisch geladen: neue Feature-Kataloge
 *   (locales/de/<feature>.json + locales/en/<feature>.json) registrieren sich selbst.
 * - Sprache wird per localStorage persistiert + aus dem Browser erkannt.
 */
import i18n from 'i18next'
import { initReactI18next } from 'react-i18next'
import LanguageDetector from 'i18next-browser-languagedetector'

export const SUPPORTED_LANGUAGES = ['de', 'en'] as const
export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number]
export const LANGUAGE_STORAGE_KEY = 'ht-lang'
/**
 * Issue #55 — LANGUAGE_STORAGE_KEY wird vom LanguageDetector bei JEDER
 * Sprachaenderung neu geschrieben, auch beim automatischen Erst-Erkennen aus
 * dem Browser. Ein gesetzter LANGUAGE_STORAGE_KEY beweist also NICHT, dass
 * der User bewusst eine Sprache gewaehlt hat. Dieser separate Marker wird nur
 * gesetzt, wenn der User aktiv am Sprachumschalter war — analog zu
 * ThemeProvider's Trennung von STORAGE_KEY (explizit) und
 * BACKEND_STORAGE_KEY (weiche BE-Praeferenz).
 */
export const EXPLICIT_LANGUAGE_KEY = 'ht-lang-explicit'

/** Vom Sprachumschalter (Settings/Landing) aufzurufen, wenn der User selbst waehlt. */
export function markLanguageExplicit(): void {
  try {
    localStorage.setItem(EXPLICIT_LANGUAGE_KEY, '1')
  } catch {
    // Private-Mode / blockierter Storage — die Wahl gilt nur für die Sitzung.
  }
}

/**
 * Wendet `users.locale` (GET /api/v2/account/me) als weiche Praeferenz an,
 * analog ThemeProvider.applyBackendTheme: NUR wenn der User noch keine
 * explizite Sprachwahl getroffen hat, und rein additiv (kein eigener
 * localStorage-Write), damit ein spaeterer Boot die BE-Praeferenz erneut
 * anwenden kann, falls sich der Account veraendert.
 */
export function applyAccountLocale(locale: string | null | undefined): void {
  if (!locale) return
  try {
    if (localStorage.getItem(EXPLICIT_LANGUAGE_KEY)) return
  } catch {
    // Storage nicht lesbar — im Zweifel nicht überschreiben.
    return
  }
  const normalized = locale.toLowerCase()
  if (!(SUPPORTED_LANGUAGES as readonly string[]).includes(normalized)) return
  if ((i18n.resolvedLanguage ?? i18n.language) === normalized) return
  void i18n.changeLanguage(normalized)
}

export const LANGUAGE_LABELS: Record<SupportedLanguage, string> = {
  de: 'Deutsch',
  en: 'English',
}

// Alle Locale-JSONs eager laden. Keys sehen aus wie './locales/de/common.json'.
const modules = import.meta.glob('./locales/*/*.json', { eager: true }) as Record<
  string,
  { default: Record<string, unknown> }
>

const resources: Record<string, Record<string, Record<string, unknown>>> = {}
for (const [path, mod] of Object.entries(modules)) {
  const match = path.match(/\.\/locales\/([^/]+)\/([^/]+)\.json$/)
  if (!match) continue
  const [, lng, ns] = match
  resources[lng] ??= {}
  resources[lng][ns] = mod.default
}

const namespaces = Object.keys(resources.de ?? { common: {} })

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    fallbackLng: 'de',
    supportedLngs: [...SUPPORTED_LANGUAGES],
    ns: namespaces,
    defaultNS: 'common',
    fallbackNS: 'common',
    detection: {
      order: ['localStorage', 'navigator'],
      lookupLocalStorage: LANGUAGE_STORAGE_KEY,
      caches: ['localStorage'],
    },
    interpolation: { escapeValue: false },
    returnNull: false,
  })

export default i18n
